import { Record, Map, List } from "immutable"

import * as helpCenterAPI from "./helpCenterAPI"
import { findPath } from "./utils"
import cuckoo from "../../../services/cuckoo/cuckoo"
import { helpcenterApiUrlSelector } from "../../../services/branding/selectors"
import { getLanguageInfo } from "../../../services/staticData"
import { FEEDBACK_COMMENT_FIELD } from "../../../consts/helpCenter"
import { getLanguage } from "../../../services/options/reducer"
import { isMobileSelector } from "../../../services/window"

const SET_SEARCH_PHRASE = "helpCenter/SET_SEARCH_PHRASE"
const SET_SEARCH_PHRASE_REQUEST = "helpCenter/SET_SEARCH_PHRASE_REQUEST"
const SET_SEARCH_PHRASE_SUCCESS = "helpCenter/SET_SEARCH_PHRASE_SUCCESS"
const SET_SEARCH_PHRASE_FAILURE = "helpCenter/SET_SEARCH_PHRASE_FAILURE"
const TOGGLE_ARTICLE_SEARCH = "helpCenter/TOGGLE_ARTICLE_SEARCH"
const RESET_ARTICLE_SEARCH = "helpCenter/RESET_ARTICLE_SEARCH"
const LOAD_CATEGORIES = "helpCenter/LOAD_CATEGORIES"
const LOAD_CATEGORIES_SUCCESS = "helpCenter/LOAD_CATEGORIES_SUCCESS"
const LOAD_CATEGORIES_FAILURE = "helpCenter/LOAD_CATEGORIES_FAILURE"
const LOAD_SUGGESTED = "helpCenter/LOAD_SUGGESTED"
const LOAD_SUGGESTED_SUCCESS = "helpCenter/LOAD_SUGGESTED_SUCCESS"
const LOAD_SUGGESTED_FAILURE = "helpCenter/LOAD_SUGGESTED_FAILURE"
const LOAD_ARTICLE = "helpCenter/LOAD_ARTICLE"
const LOAD_ARTICLE_SUCCESS = "helpCenter/LOAD_ARTICLE_SUCCESS"
const LOAD_ARTICLE_FAILURE = "helpCenter/LOAD_ARTICLE_FAILURE"
const LOADING = "helpCenter/LOADING"
const VOTE = "helpCenter/VOTE"
const VOTE_SUCCESS = "helpCenter/VOTE_SUCCESS"
const VOTE_FAILURE = "helpCenter/VOTE_FAILURE"
const COMMENT_SAVE = "helpCenter/COMMENT_SAVE"
const COMMENT = "helpCenter/COMMENT"
const COMMENT_SUCCESS = "helpCenter/COMMENT_SUCCESS"
const COMMENT_FAILURE = "helpCenter/COMMENT_FAILURE"
const SET_VIEW = "helpCenter/SET_VIEW"
const CLOSE_WIDGET = "helpCenter/CLOSE_WIDGET"
const RESET_WIDGET = "helpCenter/RESET_WIDGET"
const HELP_CENTER_TOGGLE = "helpCenter/HELP_CENTER_TOGGLE"
const FORWARD = "helpCenter/FORWARD"
const BACK = "helpCenter/BACK"
const TOGGLE_PHONE_MODAL = "helpCenter/TOGGLE_PHONE_MODAL"

const DefaultSearchState = Record({
  text: "",
  error: null,
  loading: null,
  results: new List(),
  showSearch: false,
})

const DefaultWidgetState = Record({
  open: false,
  view: null, // articleId
})

const HelpCenterState = Record({
  categories: Map({
    loading: false,
    error: false,
    success: false,
    data: null,
    articles: Map({}),
  }),
  suggested: Map({
    loading: false,
    error: false,
    success: false,
    data: null,
  }),
  phoneModalOpened: false,
  history: new List(),
  search: new DefaultSearchState(),
  widget: new DefaultWidgetState(),
})

export default function(state = new HelpCenterState(), action) {
  if (!(state instanceof HelpCenterState)) {
    const helpCenterState = new HelpCenterState()
    // just so i dont have to create separate class for every part of the state, it looks terrible
    const categories = state.getIn(["categories", "data"])
    const articles = state.getIn(["categories", "articles"])
    const newArticles = {}
    Object.keys((articles && articles.toJS()) || {}).map(key => {
      newArticles[key] = Map(articles.toJS()[key])
      return true
    })
    const suggested = state.getIn(["suggested", "data"])

    return helpCenterState
      .setIn(["categories", "data"], categories && categories.toJS())
      .setIn(["categories", "loading"], state.getIn(["categories", "loading"]))
      .setIn(["categories", "error"], state.getIn(["categories", "error"]))
      .setIn(["categories", "articles"], Map(newArticles))
      .setIn(["suggested", "data"], suggested && suggested.toJS())
  }

  switch (action.type) {
    case SET_SEARCH_PHRASE:
      return state.setIn(["search", "text"], action.text)

    case SET_SEARCH_PHRASE_REQUEST:
      return state.setIn(["search", "loading"], true).setIn(["search", "error"], false)

    case SET_SEARCH_PHRASE_SUCCESS:
      return state
        .setIn(["search", "loading"], false)
        .setIn(["search", "results"], new List(action.res))

    case SET_SEARCH_PHRASE_FAILURE:
      return state.setIn(["search", "loading"], false).setIn(["search", "error"], true)

    case LOAD_CATEGORIES_SUCCESS:
      return state
        .setIn(["categories", "data"], action.res)
        .setIn(["categories", "error"], false)
        .setIn(["categories", "success"], true)

    case LOAD_CATEGORIES_FAILURE:
      return state.setIn(["categories", "error"], true).setIn(["categories", "success"], false)

    case LOAD_SUGGESTED_SUCCESS:
      return state
        .setIn(["suggested", "data"], action.res)
        .setIn(["suggested", "error"], false)
        .setIn(["suggested", "success"], true)

    case LOAD_SUGGESTED_FAILURE:
      return state.setIn(["suggested", "error"], true).setIn(["suggested", "success"], false)

    case LOADING:
      return state.setIn(action.path, action.value)

    case LOAD_ARTICLE_SUCCESS:
      return state.setIn(
        ["categories", "articles", action.id],
        Map({
          data: action.res,
          url: action.url,
          error: false,
        }),
      )

    case LOAD_ARTICLE_FAILURE:
      return state.setIn(
        ["categories", "articles", action.id],
        Map({
          error: true,
        }),
      )

    case VOTE_SUCCESS:
      return state.setIn(
        ["categories", "articles", action.id, "vote"],
        Map({
          success: true,
          error: false,
          voted: action.voted,
        }),
      )

    case VOTE_FAILURE:
      return state
        .setIn(["categories", "articles", action.id, "vote", "error"], true)
        .setIn(["categories", "articles", action.id, "vote", "success"], false)

    case COMMENT_SAVE:
      return state.setIn(
        ["categories", "articles", action.id, action.field, "commented"],
        action.commented,
      )

    case COMMENT_SUCCESS:
      return state.setIn(
        ["categories", "articles", action.id, action.field],
        Map({
          success: true,
          error: false,
          commented: action.commented,
        }),
      )

    case COMMENT_FAILURE:
      return state
        .setIn(["categories", "articles", action.id, action.field, "error"], true)
        .setIn(["categories", "articles", action.id, action.field, "success"], false)

    case SET_VIEW:
      return state.setIn(["widget", "view"], action.id)

    case HELP_CENTER_TOGGLE:
      return state.mergeIn(["widget"], { open: action.open, view: action.id })

    case CLOSE_WIDGET:
      return state.set("widget", new DefaultWidgetState()).set("search", new DefaultSearchState())

    case RESET_WIDGET:
      return state.set("search", new DefaultSearchState())

    case RESET_ARTICLE_SEARCH:
      return state.setIn(["search", "showSearch"], false)

    case TOGGLE_ARTICLE_SEARCH:
      return state.setIn(["search", "showSearch"], !state.getIn(["search", "showSearch"]))

    case FORWARD:
      return state.set("history", state.get("history").push(action.url))

    case BACK:
      return state.set("history", state.get("history").pop())

    case TOGGLE_PHONE_MODAL:
      return state.set("phoneModalOpened", !state.get("phoneModalOpened"))

    default:
      return state
  }
}

export const loadArticle = id => async (dispatch, getState) => {
  const apiUrl = helpcenterApiUrlSelector(getState())
  const url = `/articles/${id}`

  dispatch({
    type: LOADING,
    path: ["categories", "articles", id, "loading"],
    value: true,
  })

  const promise = helpCenterAPI.getArticle(apiUrl, id, getLanguageInfo(getState()))

  const ok = await dispatch({
    action: LOAD_ARTICLE,
    type: LOAD_ARTICLE,
    promise,
    id,
    url,
  })

  await dispatch({
    type: LOADING,
    path: ["categories", "articles", id, "loading"],
    value: false,
  })

  return ok
}

export const loadCategories = (lang, articleId, categoryId) => async (dispatch, getState) => {
  const apiUrl = helpcenterApiUrlSelector(getState())

  dispatch({
    type: LOADING,
    path: ["categories", "loading"],
    value: true,
  })

  const promise = helpCenterAPI.langFetch(apiUrl, "/categories/1", getLanguageInfo(getState()))

  const ok = await dispatch({
    action: LOAD_CATEGORIES,
    type: LOAD_CATEGORIES,
    promise,
  })

  await dispatch({
    type: LOADING,
    path: ["categories", "loading"],
    value: false,
  })

  if ((!articleId && !categoryId) || !ok) return ok

  return promise.then(data => {
    const path = findPath(new Map({ data }), articleId)
    return dispatch(loadArticle(path[path.length - 1], lang))
  })
}

export const loadSuggested = () => async (dispatch, getState) => {
  const apiUrl = helpcenterApiUrlSelector(getState())

  dispatch({
    type: LOADING,
    path: ["suggested", "loading"],
    value: true,
  })

  const promise = helpCenterAPI.langFetch(apiUrl, "/categories/2", getLanguageInfo(getState()))

  const ok = await dispatch({
    action: LOAD_SUGGESTED,
    type: LOAD_SUGGESTED,
    promise,
  })

  await dispatch({
    type: LOADING,
    path: ["suggested", "loading"],
    value: false,
  })

  return ok
}

export const setSearchPhrase = (text, lang, widget) => async (dispatch, getState) => {
  const apiUrl = helpcenterApiUrlSelector(getState())
  cuckoo.infinario("helpCenterAction", {
    action: "searchHappened",
    where: widget ? "widget" : "helpCenter",
  })

  dispatch({
    type: SET_SEARCH_PHRASE,
    text,
  })

  const ok = await dispatch({
    type: SET_SEARCH_PHRASE,
    promise: helpCenterAPI.langFetch(
      apiUrl,
      `/search?q=${text}&autocomplete&tree_ids=1`,
      getLanguageInfo(getState()),
    ),
  })
  return ok
}

export function setView(id) {
  return {
    type: SET_VIEW,
    id,
  }
}

export const vote = (id, value) => async (dispatch, getState) => {
  const apiUrl = helpcenterApiUrlSelector(getState())

  dispatch({
    type: LOADING,
    path: ["categories", "articles", id, "vote", "loading"],
    value: true,
  })

  const promise = helpCenterAPI.vote(apiUrl, id, value)

  const ok = await dispatch({
    action: VOTE,
    type: VOTE,
    promise,
    id,
    voted: value,
  })

  await dispatch({
    type: LOADING,
    path: ["categories", "articles", id, "vote", "loading"],
    value: false,
  })

  return ok
}

export const commentSave = (id, value, field = FEEDBACK_COMMENT_FIELD.REASON) => dispatch => {
  dispatch({
    type: COMMENT_SAVE,
    id,
    commented: value,
    field,
  })
}

export const comment = (id, value, field = FEEDBACK_COMMENT_FIELD.REASON) => async (
  dispatch,
  getState,
) => {
  const apiUrl = helpcenterApiUrlSelector(getState())

  dispatch({
    type: LOADING,
    path: ["categories", "articles", id, field, "loading"],
    value: true,
  })

  const promise = helpCenterAPI.comment(apiUrl, id, value, field)

  const ok = await dispatch({
    action: COMMENT,
    type: COMMENT,
    promise,
    id,
    commented: value,
    field,
  })

  await dispatch({
    type: LOADING,
    path: ["categories", "articles", id, field, "loading"],
    value: false,
  })

  return ok
}

/*
 * Opens/closes help center widget
 *
 * open    open (true) or close (false) widget
 * id    ID of article to load instead of suggested articles; optional
 */
export function toggleHelpCenter(open, id) {
  return {
    type: HELP_CENTER_TOGGLE,
    open,
    id,
  }
}

/**
 * Respects mobile/desktop and language.
 */
export function openHelpCenterArticle(articleId) {
  return (dispatch, getState) => {
    const state = getState()
    const isMobile = isMobileSelector(state)
    const language = getLanguage(state)

    if (isMobile) {
      window.location.href = `/${language}/helpcenter/search/article/${articleId}?return=${encodeURIComponent(
        window.location.href,
      )}`
    } else {
      dispatch(toggleHelpCenter(true, articleId))
    }
  }
}

export function closeWidget() {
  return {
    type: CLOSE_WIDGET,
  }
}

export function resetWidget() {
  return {
    type: RESET_WIDGET,
  }
}

export function toggleArticleSearch() {
  return {
    type: TOGGLE_ARTICLE_SEARCH,
  }
}

export function resetArticleSearch() {
  return {
    type: RESET_ARTICLE_SEARCH,
  }
}

export function back() {
  return {
    type: BACK,
  }
}

export function forward(url) {
  return {
    type: FORWARD,
    url,
  }
}

export function togglePhoneModal() {
  return {
    type: TOGGLE_PHONE_MODAL,
  }
}
